<template>
  <div
    v-show="!(isErrorNoDisplay && state === -1)"
    class="base-img"
    :style="rootStyle"
    :class="[state !== 1 && 'is-no-image', {
      'page-hina': isHina,
      'page-jps': isJps,
    }]"
  >
    <div v-if="isLoading" class="jps-img-loading" />
    <div v-show="!isLoading" class="jps-img-cont">
      <img
        ref="imgTag"
        :key="imgReloadCount"
        :src="src"
        :alt="alt"
        :style="imgStyle"
        @load="loadHandling"
        @error="errorHandling"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
const { defaultNoImage } = useNoImage()
const { isHina, isJps } = useServiceType()

type Props = {
  src?: string
  w?: number
  h?: number
  natural?: boolean
  cover?: boolean
  background?: string
  alt?: string
  errorAlt?: string
  isErrorNoDisplay?: boolean
}
type Emits = {
  (e: 'stateChanged', value: number): void,
}
const props = withDefaults(defineProps<Props>(), {
  natural: false,
  cover: false,
  alt: null,
  isErrorNoDisplay: false,
})
const emits = defineEmits<Emits>()
onMounted(() => {
  if (props.src == null) {
    errorHandling()
  } else {
    startImgReload()
  }
})

/**
 * 0:読み込み中
 * 1:読み込み完了
 * -1:読み込みエラー
 */
const state: Ref<number> = ref(0)
watch(
  () => props.src,
  () => {
    state.value = 0
    stopImgReload()
    imgReloadCount.value = 0
    isErrorImageLoadError = false
    imgTag.value.style.display = 'flex'
    if (props.src == null) {
      errorHandling()
    } else {
      startImgReload()
    }
  }
)
watch(() => state.value, (newValue) => {
  emits('stateChanged', newValue)
})

const isLoading = computed(() => {
  return state.value === 0
})

const loadHandling = (e) => {
  if (state.value == 0) {
    state.value = 1
    stopImgReload()
  }
}

let isErrorImageLoadError = false
const errorHandling = () => {
  if (state.value == 0) {
    state.value = -1
    stopImgReload()
  }
  if (isErrorImageLoadError) {
    return
  }
  if (imgTag.value) {
    nextTick(() => {
      // エラー画像の読み込み自体に失敗した場合は画像の表示をあきらめるためにフラグを立てておく
      isErrorImageLoadError = true
      if (props.errorAlt) {
        imgTag.value.src = props.errorAlt
      } else {
        imgTag.value.src = defaultNoImage
      }
    })
  }
}

// スタイル
const rootStyle = computed(() => {
  let style = {}
  if (props.w) {
    style['width'] = props.w + 'px'
  }
  if (props.h) {
    style['height'] = props.h + 'px'
  }
  if (props.background) {
    style['background'] = props.background
  }
  return style
})

const imgTag = ref<HTMLImageElement | null>()
const imgStyle = computed(() => {
  let style = {}
  let fit = 'contain'
  if (state.value == 1 && props.cover) {
    fit = 'cover'
  }
  style['object-fit'] = fit

  if (props.natural && !props.cover && state.value == 1 && imgTag) {
    if (!props.w) {
      style['width'] = imgTag.value.naturalWidth + 'px'
    }
    if (!props.h) {
      style['height'] = imgTag.value.naturalHeight + 'px'
    }
  }
  return style
})

let imgReloadTimer = null
let imgReloadCount: Ref<number> = ref(0)
const startImgReload = () => {
  imgReloadTimer = setInterval(() => {
    if (imgReloadCount.value > 4 || state.value != 0) {
      stopImgReload()
    } else {
      imgReloadCount.value++
    }
  }, 250)
}
const stopImgReload = () => {
  if (imgReloadTimer) {
    clearInterval(imgReloadTimer)
  }
}
</script>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

.base-img {
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;

  .jps-img-loading {
    height: 2rem;
    width: 2rem;
    animation: rotate 2s infinite ease;
    border: 2px solid $jps-gray-300;
    border-right-color: transparent;
    border-radius: 50%;
    @keyframes rotate {
      0% {
        transform: rotate(0deg);
      }
      100% {
        transform: rotate(360deg);
      }
    }
  }

  .jps-img-cont {
    overflow: hidden;
    text-align: left !important; //text-align centerなどを上から食らうと、ずれの計算が壊れる

    width: 100%;
    height: 100%;
    display: flex;
    justify-content:center;
    align-items:center;
    img {
      transform-origin: 0px 0px 0px !important;
      width: 100%;
      height: 100%;
    }
  }
}

.page-hina {
  background-color: $white;

  &.is-no-image {
    background-color: $hina-background-cream;
  }
}
</style>
